import React, { useState, useMemo, useCallback } from "react";
import { ClipOverlay, ZoomConfig, ZoomKeyframe, ZOOM_PRESETS, ZoomPresetKey, ZoomAnimationStyle } from "../../../types";
import { createZoomKeyframe, validateKeyframes } from "../../../utils/zoom/zoom-calculator";
import { ZoomTimeline } from "./zoom-timeline";
import { ZoomFocusPicker } from "./zoom-focus-picker";
import { Slider } from "../../ui/slider";
import { Switch } from "../../ui/switch";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Trash2, ZoomIn, Move } from "lucide-react";
import { FPS } from "../../../../../constants";

interface ZoomKeyframePanelProps {
  localOverlay: ClipOverlay;
  currentFrame: number;
  onZoomConfigChange: (zoomConfig: ZoomConfig) => void;
}

const ANIMATION_STYLE_OPTIONS: { value: ZoomAnimationStyle; label: string }[] = [
  { value: "both", label: "Entry & Exit" },
  { value: "entry-only", label: "Entry Only" },
  { value: "exit-only", label: "Exit Only" },
  { value: "none", label: "No Animation (Instant)" },
];

/**
 * ZoomKeyframePanel provides UI for managing zoom keyframes on video overlays.
 * Features:
 * - Enable/disable zoom toggle
 * - Visual timeline with context menu for quick zoom creation
 * - Draggable focus position picker
 * - Animation style options (entry, exit, both, none)
 */
export const ZoomKeyframePanel: React.FC<ZoomKeyframePanelProps> = ({
  localOverlay,
  currentFrame,
  onZoomConfigChange,
}) => {
  const [selectedKeyframeId, setSelectedKeyframeId] = useState<string | null>(null);
  const [showFocusPicker, setShowFocusPicker] = useState(false);

  const zoomConfig = localOverlay.zoom ?? {
    enabled: false,
    keyframes: [],
    transitionDuration: 15,
  };

  // Calculate relative frame within overlay
  const relativeFrame = Math.max(0, currentFrame - localOverlay.from);

  // Get selected keyframe
  const selectedKeyframe = useMemo(() => {
    return zoomConfig.keyframes.find(kf => kf.id === selectedKeyframeId);
  }, [zoomConfig.keyframes, selectedKeyframeId]);

  const handleToggleZoom = (enabled: boolean) => {
    onZoomConfigChange({
      ...zoomConfig,
      enabled,
    });
  };

  const handleAddKeyframe = useCallback((startFrame: number, endFrame: number, scale: number = 1.5) => {
    const newKeyframe = createZoomKeyframe(
      startFrame,
      endFrame,
      scale,
      50,  // center X
      50   // center Y
    );

    const newKeyframes = [...zoomConfig.keyframes, newKeyframe].sort(
      (a, b) => a.startFrame - b.startFrame
    );

    const validation = validateKeyframes(newKeyframes);
    if (!validation.valid) {
      console.warn("Cannot add keyframe:", validation.error);
      return;
    }

    onZoomConfigChange({
      ...zoomConfig,
      keyframes: newKeyframes,
    });
    setSelectedKeyframeId(newKeyframe.id);
  }, [zoomConfig, onZoomConfigChange]);

  const handleDeleteKeyframe = useCallback((keyframeId: string) => {
    onZoomConfigChange({
      ...zoomConfig,
      keyframes: zoomConfig.keyframes.filter(kf => kf.id !== keyframeId),
    });
    if (selectedKeyframeId === keyframeId) {
      setSelectedKeyframeId(null);
    }
  }, [zoomConfig, onZoomConfigChange, selectedKeyframeId]);

  const handleUpdateKeyframe = useCallback((keyframeId: string, updates: Partial<ZoomKeyframe>) => {
    const newKeyframes = zoomConfig.keyframes.map(kf =>
      kf.id === keyframeId ? { ...kf, ...updates } : kf
    );

    const validation = validateKeyframes(newKeyframes);
    if (!validation.valid) {
      console.warn("Invalid keyframe update:", validation.error);
      return;
    }

    onZoomConfigChange({
      ...zoomConfig,
      keyframes: newKeyframes,
    });
  }, [zoomConfig, onZoomConfigChange]);

  const getPresetFromScale = (scale: number): ZoomPresetKey | "custom" => {
    for (const [key, value] of Object.entries(ZOOM_PRESETS)) {
      if (Math.abs(value - scale) < 0.01) {
        return key as ZoomPresetKey;
      }
    }
    return "custom";
  };

  const formatFrameToTime = (frame: number): string => {
    const seconds = frame / FPS;
    return `${seconds.toFixed(1)}s`;
  };

  return (
    <div className="space-y-4">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ZoomIn className="w-4 h-4 text-muted-foreground" />
          <label className="text-sm font-extralight">Enable Zoom</label>
        </div>
        <Switch
          checked={zoomConfig.enabled}
          onCheckedChange={handleToggleZoom}
        />
      </div>

      {zoomConfig.enabled && (
        <>
          {/* Visual Timeline */}
          <ZoomTimeline
            keyframes={zoomConfig.keyframes}
            overlayDuration={localOverlay.durationInFrames}
            currentFrame={relativeFrame}
            onKeyframeUpdate={handleUpdateKeyframe}
            onAddKeyframe={handleAddKeyframe}
            onDeleteKeyframe={handleDeleteKeyframe}
            onSelectKeyframe={setSelectedKeyframeId}
            selectedKeyframeId={selectedKeyframeId}
          />

          {/* Selected Keyframe Editor */}
          {selectedKeyframe && (
            <div className="rounded-md border p-3 space-y-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium">
                  {formatFrameToTime(selectedKeyframe.startFrame)} - {formatFrameToTime(selectedKeyframe.endFrame)}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteKeyframe(selectedKeyframe.id)}
                  className="h-6 w-6 p-0"
                >
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>

              {/* Scale Preset */}
              <div className="space-y-2">
                <label className="text-xs font-extralight">Zoom Level</label>
                <Select
                  value={getPresetFromScale(selectedKeyframe.scale)}
                  onValueChange={(value) => {
                    if (value !== "custom") {
                      handleUpdateKeyframe(selectedKeyframe.id, {
                        scale: ZOOM_PRESETS[value as ZoomPresetKey],
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full text-xs shadow-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (1x)</SelectItem>
                    <SelectItem value="shallow">Shallow (1.5x)</SelectItem>
                    <SelectItem value="moderate">Moderate (2x)</SelectItem>
                    <SelectItem value="maximum">Deep (3x)</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Scale Slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-extralight">Scale</label>
                  <span className="text-xs font-medium">{selectedKeyframe.scale.toFixed(1)}x</span>
                </div>
                <Slider
                  value={[selectedKeyframe.scale]}
                  onValueChange={([value]) =>
                    handleUpdateKeyframe(selectedKeyframe.id, { scale: value })
                  }
                  min={1}
                  max={4}
                  step={0.1}
                  className="flex-1"
                />
              </div>

              {/* Focus Position Picker Toggle */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-extralight">Focus Position</label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFocusPicker(!showFocusPicker)}
                    className="h-7 text-xs"
                  >
                    <Move className="w-3 h-3 mr-1" />
                    {showFocusPicker ? "Hide Picker" : "Pick Focus"}
                  </Button>
                </div>

                {/* Focus Position Picker */}
                {showFocusPicker && (
                  <ZoomFocusPicker
                    xPct={selectedKeyframe.xPct}
                    yPct={selectedKeyframe.yPct}
                    scale={selectedKeyframe.scale}
                    videoSrc={localOverlay.src}
                    onPositionChange={(xPct, yPct) =>
                      handleUpdateKeyframe(selectedKeyframe.id, { xPct, yPct })
                    }
                    onScaleChange={(scale) =>
                      handleUpdateKeyframe(selectedKeyframe.id, { scale })
                    }
                  />
                )}

                {/* Position Sliders (always visible) */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] text-muted-foreground">X</label>
                      <span className="text-[10px]">{Math.round(selectedKeyframe.xPct)}%</span>
                    </div>
                    <Slider
                      value={[selectedKeyframe.xPct]}
                      onValueChange={([value]) =>
                        handleUpdateKeyframe(selectedKeyframe.id, { xPct: value })
                      }
                      min={0}
                      max={100}
                      step={1}
                      className="flex-1"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] text-muted-foreground">Y</label>
                      <span className="text-[10px]">{Math.round(selectedKeyframe.yPct)}%</span>
                    </div>
                    <Slider
                      value={[selectedKeyframe.yPct]}
                      onValueChange={([value]) =>
                        handleUpdateKeyframe(selectedKeyframe.id, { yPct: value })
                      }
                      min={0}
                      max={100}
                      step={1}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Animation Style */}
              <div className="space-y-2">
                <label className="text-xs font-extralight">Animation Style</label>
                <Select
                  value={selectedKeyframe.animationStyle ?? "both"}
                  onValueChange={(value) =>
                    handleUpdateKeyframe(selectedKeyframe.id, {
                      animationStyle: value as ZoomAnimationStyle,
                    })
                  }
                >
                  <SelectTrigger className="w-full text-xs shadow-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ANIMATION_STYLE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Controls how zoom transitions in and out
                </p>
              </div>
            </div>
          )}

          {/* No selection hint */}
          {zoomConfig.keyframes.length > 0 && !selectedKeyframe && (
            <p className="text-xs text-muted-foreground text-center py-2">
              Click a zoom block to edit its settings
            </p>
          )}

          {/* Empty state */}
          {zoomConfig.keyframes.length === 0 && (
            <div className="text-center py-2 text-muted-foreground">
              <p className="text-xs">Right-click on timeline to add zoom effect</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
